import { useQuery } from '@tanstack/react-query';
import {
  ACCESS_LEVEL_LABELS,
  ACCESS_LEVELS,
  APP_MODULES,
  type CompanyRolesResponse,
  type ModuleDefinition,
  type UserResponse,
} from '@k-management/shared';
import { useState } from 'react';
import { DataGrid, type GridColumn } from '../../components/DataGrid.js';
import { useCommands } from '../../components/layout/page-chrome.js';
import { UserDialog } from '../../components/UserDialog.js';
import { apiRequest } from '../../lib/api.js';
import { formatTimestamp } from '../../lib/format.js';
import { queryKeys } from '../../lib/query-keys.js';
import { useListFilters } from '../../lib/list-filters.js';

const MODULES: readonly ModuleDefinition[] = APP_MODULES;

/** Administrators: who may use K-Management, their profile and what they reach (docs/adr/ADR-005, ADR-008). */
export function UsersPage() {
  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => apiRequest<UserResponse[]>('/users'),
  });
  // The company roles of each user (Organizzazione › Ruoli, decision of 04/10/2026).
  const roles = useQuery({
    queryKey: queryKeys.companyRoles,
    queryFn: () => apiRequest<CompanyRolesResponse>('/organization/roles'),
  });
  const rolesOf = (userId: string) => {
    const all = roles.data?.roles ?? [];
    return {
      holds: all.filter((role) => role.holders.some((person) => person.userId === userId)),
      stands: all.filter((role) => role.substitute?.userId === userId),
    };
  };
  const [editing, setEditing] = useState<UserResponse | 'new' | null>(null);
  // The filters stay while the browser is open: found again coming back to the list (1.61).
  const { filters, setFilter, reset, changed } = useListFilters('users', {
    status: 'active' as 'active' | 'all',
  });
  const { status } = filters;
  const setStatus = (value: 'active' | 'all') => setFilter('status', value);

  useCommands([
    {
      key: 'new',
      label: 'Nuovo utente',
      icon: 'plus',
      primary: true,
      onClick: () => setEditing('new'),
    },
  ]);

  const columns: GridColumn<UserResponse>[] = [
    {
      key: 'name',
      label: 'Cognome e nome',
      required: true,
      value: (user) => `${user.lastName} ${user.firstName}`,
      render: (user) => (
        <>
          <button type="button" className="link strong" onClick={() => setEditing(user)}>
            {user.lastName} {user.firstName}
          </button>
          {user.isAdmin && <span className="tag">Amministratore</span>}
        </>
      ),
    },
    { key: 'email', label: 'E-mail', value: (user) => user.email, render: (user) => user.email },
    {
      key: 'signIn',
      label: 'Accesso con',
      value: (user) => signInLabel(user),
      render: (user) => signInLabel(user),
    },
    {
      key: 'profile',
      label: 'Profilo',
      value: (user) => profileLabel(user),
      render: (user) => (
        <>
          {profileLabel(user)}
          {!user.isAdmin && user.profileName && hasExceptions(user) && (
            <span className="tag">eccezioni</span>
          )}
        </>
      ),
    },
    {
      key: 'companyRoles',
      label: 'Ruoli aziendali',
      value: (user) =>
        rolesOf(user.id)
          .holds.map((role) => role.title)
          .join(', '),
      render: (user) => {
        const { holds, stands } = rolesOf(user.id);
        if (holds.length === 0 && stands.length === 0) return <span className="muted">—</span>;
        return (
          <span className="chips">
            {holds.map((role) => (
              <span key={role.code} className="pill brand">
                {role.title}
              </span>
            ))}
            {stands.map((role) => (
              <span key={`s-${role.code}`} className="pill muted">
                sostituto {role.title}
              </span>
            ))}
          </span>
        );
      },
    },
    ...MODULES.map((module): GridColumn<UserResponse> => ({
      key: `module.${module.key}`,
      label: module.label,
      value: (user) => moduleSummary(user, module),
      render: (user) => moduleSummary(user, module),
    })),
    {
      key: 'lastSeen',
      label: 'Ultimo accesso',
      value: (user) => user.lastSeenAt ?? '',
      render: (user) => (user.lastSeenAt ? formatTimestamp(user.lastSeenAt) : '—'),
    },
    {
      key: 'status',
      label: 'Stato',
      value: (user) => (user.isActive ? 'Attivo' : 'Disattivato'),
      render: (user) => (
        <span className={user.isActive ? 'pill ok' : 'pill muted'}>
          {user.isActive ? 'Attivo' : 'Disattivato'}
        </span>
      ),
    },
  ];

  const rows = (users.data ?? []).filter((user) => status === 'all' || user.isActive);

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Utenti</h1>
          <p className="muted">
            Chi entra in K-Management, con quale profilo di accesso e con quali eccezioni.
          </p>
        </div>
      </div>

      {users.isPending && <p className="muted">Caricamento…</p>}
      {users.isError && <p className="error">{users.error.message}</p>}
      {users.data && (
        <DataGrid
          id="users"
          filtersChanged={changed}
          onResetFilters={reset}
          columns={columns}
          rows={rows}
          rowKey={(user) => user.id}
          searchPlaceholder="Cerca per nome, e-mail o profilo"
          rowClassName={(user) => (user.isActive ? undefined : 'inactive')}
          onRowOpen={(user) => setEditing(user)}
          toolbar={
            <div className="chips" role="group" aria-label="Stato">
              {(
                [
                  ['active', 'Attivi'],
                  ['all', 'Tutti'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={status === value ? 'chip selected' : 'chip'}
                  onClick={() => setStatus(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          }
          actions={(user) => (
            <button type="button" className="link" onClick={() => setEditing(user)}>
              Modifica
            </button>
          )}
        />
      )}

      {editing && (
        <UserDialog user={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function signInLabel(user: UserResponse): string {
  return user.signInMethod === 'microsoft' ? 'Microsoft 365' : 'E-mail e password';
}

function profileLabel(user: UserResponse): string {
  return user.isAdmin ? '—' : (user.profileName ?? 'Personalizzato');
}

/** Effective level and data scope of a user in a module, from their permissions. */
function moduleSummary(user: UserResponse, module: ModuleDefinition): string {
  if (user.isAdmin) return 'Completo';
  const level = [...ACCESS_LEVELS]
    .reverse()
    .find(
      (candidate) =>
        candidate !== 'none' && user.permissions.includes(`${module.key}.${candidate}` as never),
    );
  if (!level) return ACCESS_LEVEL_LABELS.none;
  const scope = (user.scopes as Record<string, 'all' | 'own' | undefined>)[module.key];
  return module.dataScope && scope === 'own'
    ? `${ACCESS_LEVEL_LABELS[level]}, solo i suoi`
    : ACCESS_LEVEL_LABELS[level];
}

function hasExceptions(user: UserResponse): boolean {
  return (
    Object.keys(user.access.modules).length > 0 ||
    Object.keys(user.access.featureOverrides).length > 0
  );
}
